"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.BlackjackGameLogic = void 0;
const logic_types_1 = require("./logic-types");
const command_list_serializer_1 = require("./commands/command-list-serializer");
const deal_processor_1 = require("./deal-processor");
const player_bet_command_1 = require("./commands/player-commands/player-bet-command");
const player_double_command_1 = require("./commands/player-commands/player-double-command");
const player_hit_command_1 = require("./commands/player-commands/player-hit-command");
const player_split_command_1 = require("./commands/player-commands/player-split-command");
const player_stand_command_1 = require("./commands/player-commands/player-stand-command");
const player_manager_1 = require("./players/player-manager");
const blackjack_game_state_1 = require("./blackjack-game-state");
const event_queue_1 = require("../../common/event-queue");
const auto_clear_croupier_command_1 = require("./commands/auto-commands/auto-clear-croupier-command");
const auto_clear_player_command_1 = require("./commands/auto-commands/auto-clear-player-command");
const action_bus_1 = require("../../common/action-bus");
const blackjack_analytics_data_1 = require("./blackjack-analytics-data");
class BlackjackGameLogic {
    get chipsAmount() {
        return this.blackjackState.chipsAmount;
    }
    get allInAmount() {
        return this.blackjackState.allInAmount;
    }
    getAnalyicsData() {
        return Object.assign(Object.assign({}, this.analyticsData), this.blackjackState.makeAnalyticsData());
    }
    constructor(dealer, cardIndexMapping, board, logicVersion) {
        this.dealer = dealer;
        this.board = board;
        this.logicVersion = logicVersion;
        this.logicActionBus = new action_bus_1.ActionBus();
        this.logicEventQueue = new event_queue_1.EventQueue();
        this.commandList = [];
        this.runningTasks = [];
        this.processingCommand = false;
        this.continuingRound = false;
        this.analyticsData = (0, blackjack_analytics_data_1.makeEmptyAnalyticsData)();
        this.playerManager = new player_manager_1.PlayerManager(board, this.logicEventQueue, logicVersion);
        this.blackjackState = new blackjack_game_state_1.BlackjackGameState(this.logicEventQueue, this.analyticsData);
        this.dealProcessor = new deal_processor_1.DealProcessor(board, dealer, cardIndexMapping);
        this.logicEventQueue.subscribe((event) => {
            switch (event.type) {
                case "onGameCompleted":
                    this.logicActionBus.emit("onGameCompleted");
                    break;
                case "onGameLost":
                    this.logicActionBus.emit("onGameLost");
                    break;
                case "onScoreChanged":
                    this.logicActionBus.emit("onScoreChanged", {
                        value: event.payload.value,
                    });
                    break;
                case "onRoundStateChanged":
                    this.logicActionBus.emit("onRoundStateChanged", {
                        state: event.payload.state,
                    });
                    break;
                case "onPlayerControlsChanged":
                    this.logicActionBus.emit("onPlayerControlsChanged", {
                        controls: event.payload.controls,
                    });
                    break;
                default:
                    break;
            }
        });
    }
    serializeCommandList() {
        return this.commandList.map((command) => command.serializeToString());
    }
    generateScoreData() {
        return this.blackjackState.currentScoreData;
    }
    startGame(existingGame) {
        this.blackjackState.initGame(existingGame);
        this.playerManager.resetPlayers(existingGame);
        this.logicEventQueue.enqueue({
            type: "onGameInit",
            instant: existingGame,
            payload: { board: this.board.clone() },
        });
    }
    dealDeck(amount) {
        this.executeCommand(new player_bet_command_1.PlayerBetCommand(amount));
    }
    playerStandDeck() {
        this.executeCommand(new player_stand_command_1.PlayerStandCommand());
    }
    playerHitDeck() {
        this.executeCommand(new player_hit_command_1.PlayerHitCommand());
    }
    playerDoubleDeck() {
        this.executeCommand(new player_double_command_1.PlayerDoubleCommand());
    }
    playerSplitDeck() {
        this.executeCommand(new player_split_command_1.PlayerSplitCommand());
    }
    continueRound(instant) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (this.continuingRound) {
                    return;
                }
                this.continuingRound = true;
                yield this.resetRound(instant);
                this.continuingRound = false;
            }
            catch (error) {
                this.logicActionBus.emit("onFatalError", {
                    context: "Continue blackjack round",
                    error,
                });
            }
        });
    }
    waitForAllTasksToBeCompleted() {
        return __awaiter(this, void 0, void 0, function* () {
            yield Promise.all(this.runningTasks);
            this.runningTasks.length = 0;
        });
    }
    executeCommand(command, existingDeal = false, instant = false) {
        const task = this.execute(command, existingDeal, instant);
        this.runningTasks.push(task);
    }
    execute(command, existingDeal, instant) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.processingCommand) {
                return;
            }
            try {
                if (!command.validateCommand(this.makeGameContext())) {
                    return;
                }
                this.processingCommand = true;
                this.commandList.push(command);
                this.logicActionBus.emit("onMoveExecuted", {
                    moves: this.serializeCommandList(),
                });
                const commandHash = command_list_serializer_1.CommandListSerializer.getHash(this.commandList);
                yield command.executeCommand(commandHash, existingDeal, instant, this.makeGameContext());
                this.processingCommand = false;
                this.updateControls(instant);
            }
            catch (error) {
                this.logicActionBus.emit("onFatalError", {
                    context: "Execute blackjack command",
                    error,
                });
            }
        });
    }
    makeGameContext() {
        return {
            playerManager: this.playerManager,
            gameState: this.blackjackState,
            dealProcessor: this.dealProcessor,
            eventQueue: this.logicEventQueue,
            analyticsData: this.analyticsData,
            logicVersion: this.logicVersion,
        };
    }
    resetRound(instant) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.blackjackState.roundState === logic_types_1.RoundState.Finish ||
                this.blackjackState.roundState === logic_types_1.RoundState.Deal) {
                // We have already finished the game
                return;
            }
            if (this.blackjackState.isOutOfChips) {
                this.blackjackState.setRoundState(logic_types_1.RoundState.Finish, false);
                this.logicEventQueue.enqueue({
                    type: "onGameLost",
                    instant: false,
                    payload: undefined,
                });
                return;
            }
            if (this.blackjackState.isGameCompleted) {
                this.blackjackState.setRoundState(logic_types_1.RoundState.Finish, false);
                this.logicEventQueue.enqueue({
                    type: "onGameCompleted",
                    instant: false,
                    payload: undefined,
                });
                return;
            }
            // Clear board of cards
            yield this.clearBoard(instant);
            this.playerManager.resetPlayers(instant);
            // Don't instantly queue these events
            this.blackjackState.increaseCurrentRound(false);
            this.blackjackState.setRoundState(logic_types_1.RoundState.Deal, false);
        });
    }
    clearBoard(instant) {
        return __awaiter(this, void 0, void 0, function* () {
            const autoCroupierRevealCommand = new auto_clear_croupier_command_1.AutoClearCroupierCommand(this.playerManager, this.logicEventQueue);
            yield autoCroupierRevealCommand.executeCommand("", false, instant);
            for (const player of this.playerManager.allPlayers) {
                const autoCroupierRevealCommand = new auto_clear_player_command_1.AutoClearPlayerCommand(player, this.playerManager, this.logicEventQueue);
                yield autoCroupierRevealCommand.executeCommand("", false, instant);
            }
        });
    }
    updateControls(instant) {
        const allowedControls = BlackjackGameLogic.makeAllowedControls({
            playerManager: this.playerManager,
            blackjackState: this.blackjackState,
            logicVersion: this.logicVersion,
        });
        this.blackjackState.setAllowedControls(allowedControls);
        this.logicEventQueue.enqueue({
            type: "onPlayerControlsChanged",
            instant,
            payload: { controls: allowedControls },
        });
    }
    static makeAllowedControls(data) {
        const allowedControls = [];
        if (data.blackjackState.roundState === logic_types_1.RoundState.Controls) {
            allowedControls.push(logic_types_1.PlayerControls.Stand);
            allowedControls.push(logic_types_1.PlayerControls.Hit);
            const player = data.playerManager.currentPlayer;
            if (data.logicVersion <= 1) {
                if (data.blackjackState.canDoubleBet(player.currentBet) &&
                    data.playerManager.hasSinglePlayer &&
                    player.allCards.length === 2) {
                    allowedControls.push(logic_types_1.PlayerControls.Double);
                }
            }
            else {
                // Allow double-down after split
                if (data.blackjackState.canDoubleBet(player.currentBet) &&
                    player.allCards.length === 2) {
                    allowedControls.push(logic_types_1.PlayerControls.Double);
                }
            }
            if (player.canSplit &&
                data.blackjackState.canDoubleBet(player.currentBet) &&
                !data.playerManager.hasMaximumPlayers) {
                allowedControls.push(logic_types_1.PlayerControls.Split);
            }
        }
        return allowedControls;
    }
}
exports.BlackjackGameLogic = BlackjackGameLogic;
